import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container, Row, Col, Card, CardBody, Button, Input, FormGroup, Label,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from 'reactstrap';
import { Wifi, Plus, Grid, Edit, Trash2, RefreshCw, Phone } from 'react-feather';
import CountUp from 'react-countup';
import { Breadcrumbs } from '../../AbstractElements';
import api from '../../api/prospect';
import { useProspectAuth as useAuth } from '../../Auth/ProspectAuthContext';
import { alertErro } from '../../utils/alerts';

const PALETTE = ['#7366ff', '#54ba4a', '#16C7F9', '#FFAA05', '#f73164'];

function avatarColor(str = '') {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function statusInfo(status) {
  if (status === 'connected') return { label: 'Conectado',    color: '#54ba4a', pulse: true  };
  if (status === 'banned')    return { label: 'Banido',       color: '#f44336', pulse: false };
  return                             { label: 'Desconectado', color: '#94a3b8', pulse: false };
}

export default function Conexoes() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [connections, setConnections] = useState([]);
  const [loading, setLoading]         = useState(true);

  const [qrModal, setQrModal]     = useState(null);
  const [qrData, setQrData]       = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrTimer, setQrTimer]     = useState(5);

  const [addModal, setAddModal]     = useState(false);
  const [addForm, setAddForm]       = useState({ instance_name: '', display_name: '', tenant_id: '' });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError]     = useState('');
  const [tenants, setTenants]       = useState([]);

  const [editModal, setEditModal]     = useState(null);
  const [editName, setEditName]       = useState('');
  const [editLoading, setEditLoading] = useState(false);

  const [deleteModal, setDeleteModal] = useState(null);

  const qrInterval       = useRef(null);
  const timerInterval    = useRef(null);
  const addInstanceRef   = useRef();

  const fetchConnections = useCallback(async () => {
    try {
      const { data } = await api.get('/connections');
      setConnections(data);
    } catch (e) { console.error('Erro ao carregar conexões:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConnections(); }, [fetchConnections]);
  useEffect(() => { if (addModal) setTimeout(() => addInstanceRef.current?.focus(), 100); }, [addModal]);

  // ── QR Code ───────────────────────────────────────────────────────────────

  const fetchQr = useCallback(async (connId) => {
    setQrLoading(true);
    try {
      const { data } = await api.get(`/connections/${connId}/qrcode`);
      setQrData(data);
      setQrTimer(5);
      const state = data?.instance?.state || data?.state || '';
      if (state === 'open') {
        clearInterval(qrInterval.current);
        clearInterval(timerInterval.current);
        fetchConnections();
      }
    } catch { setQrData(null); }
    finally { setQrLoading(false); }
  }, [fetchConnections]);

  function openQrModal(conn) {
    setQrModal(conn);
    setQrData(null);
    setQrTimer(5);
    fetchQr(conn.id);
    qrInterval.current    = setInterval(() => fetchQr(conn.id), 5000);
    timerInterval.current = setInterval(() => setQrTimer(t => Math.max(0, t - 1)), 1000);
  }

  function closeQrModal() {
    clearInterval(qrInterval.current);
    clearInterval(timerInterval.current);
    setQrModal(null);
    setQrData(null);
    fetchConnections();
  }

  // ── Editar ────────────────────────────────────────────────────────────────

  function openEditModal(conn) { setEditModal(conn); setEditName(conn.display_name); }

  async function handleEdit() {
    setEditLoading(true);
    try {
      await api.put(`/connections/${editModal.id}`, { display_name: editName });
      setEditModal(null);
      fetchConnections();
    } catch (e) { alertErro('Erro ao editar', e.response?.data?.error || ''); }
    finally { setEditLoading(false); }
  }

  // ── Excluir ───────────────────────────────────────────────────────────────

  async function handleDelete() {
    try {
      await api.delete(`/connections/${deleteModal.id}`);
      setDeleteModal(null);
      fetchConnections();
    } catch (e) { alertErro('Erro ao excluir', e.response?.data?.error || ''); }
  }

  // ── Adicionar ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isAdmin && addModal && tenants.length === 0) {
      api.get('/tenants').then(({ data }) => setTenants(data)).catch(() => {});
    }
  }, [isAdmin, addModal, tenants.length]);

  async function handleAdd(e) {
    e.preventDefault();
    setAddError('');
    setAddLoading(true);
    try {
      const body = { ...addForm, tenant_id: isAdmin ? addForm.tenant_id : user?.tenant_id };
      await api.post('/connections', body);
      setAddModal(false);
      setAddForm({ instance_name: '', display_name: '', tenant_id: '' });
      fetchConnections();
    } catch (e) { setAddError(e.response?.data?.error || 'Erro ao criar conexão'); }
    finally { setAddLoading(false); }
  }

  // ── Dados derivados ───────────────────────────────────────────────────────

  const total     = connections.length;
  const connected = connections.filter(c => c.status === 'connected').length;
  const qrBase64  = qrData?.base64 || (qrData?.code ? `data:image/png;base64,${qrData.code}` : null);

  return (
    <>
      <Breadcrumbs mainTitle='Conexões WhatsApp' parent='Topo Prospect' title='Conexões' />
      <Container fluid>

        {isAdmin && (
          <div className='alert alert-secondary py-2 px-3 mb-3 d-flex align-items-center gap-2' style={{ fontSize: '0.8rem', color: '#495057' }}>
            <span>🔍</span> <span>Modo auditoria — faça login como tenant para operar</span>
          </div>
        )}

        {/* Header */}
        <div className='d-flex justify-content-between align-items-center mb-4'>
          <div />
          {!isAdmin && (
            <Button color='primary' size='sm' onClick={() => setAddModal(true)}>
              <Plus size={14} className='me-1' /> Adicionar conexão
            </Button>
          )}
        </div>

        {/* ── Widget cards Cuba widget-1 ──────────────────────────────────── */}
        <Row className='mb-2'>
          <Col xl='3' md='6' sm='6' className='mb-4'>
            <Card className='widget-1'>
              <CardBody>
                <div className='widget-content'>
                  <div className='widget-round primary'>
                    <div className='bg-round' style={{ background: 'linear-gradient(26deg,#a26cf8,#cc8ffd)', overflow: 'hidden', width: 54, height: 54 }}>
                      <Wifi color='#fff' strokeWidth={1.5} style={{ width: 24, height: 24, flexShrink: 0 }} />
                    </div>
                  </div>
                  <div>
                    <h4 className='mb-1 f-w-600'>
                      <CountUp end={total} duration={1.2} />
                    </h4>
                    <span className='f-light f-12'>Total de conexões</span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl='3' md='6' sm='6' className='mb-4'>
            <Card className='widget-1'>
              <CardBody>
                <div className='widget-content'>
                  <div className='widget-round success'>
                    <div className='bg-round' style={{ background: 'linear-gradient(26deg,#54ba4a,#29d9d5)', overflow: 'hidden', width: 54, height: 54 }}>
                      <Wifi color='#fff' strokeWidth={1.5} style={{ width: 24, height: 24, flexShrink: 0 }} />
                    </div>
                  </div>
                  <div>
                    <h4 className='mb-1 f-w-600'>
                      <CountUp end={connected} duration={1.5} />
                    </h4>
                    <span className='f-light f-12'>Conectadas agora</span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Grid de Cards */}
        {loading ? (
          <div className='text-center py-5 text-muted'>Carregando...</div>
        ) : connections.length === 0 ? (
          <Card>
            <CardBody className='text-center py-5'>
              <Wifi size={48} className='mb-3' style={{ color: '#dee2e6' }} />
              <h6 className='text-muted'>Nenhuma conexão cadastrada</h6>
              {isAdmin && (
                <Button color='primary' size='sm' className='mt-2' onClick={() => setAddModal(true)}>
                  Adicionar a primeira conexão
                </Button>
              )}
            </CardBody>
          </Card>
        ) : (
          <Row>
            {connections.map(conn => {
              const color   = avatarColor(conn.display_name || conn.instance_name);
              const st      = statusInfo(conn.status);
              const initial = (conn.display_name || conn.instance_name || '?')[0].toUpperCase();
              return (
                <Col xl='4' md='6' key={conn.id} className='mb-4'>
                  <Card
                    className='h-100'
                    style={{ borderTop: `3px solid ${color}`, transition: 'transform .15s, box-shadow .15s' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                  >
                    <CardBody>
                      <div className='d-flex align-items-start justify-content-between mb-3'>
                        <div
                          className='rounded-circle d-flex align-items-center justify-content-center f-w-700'
                          style={{ width: 46, height: 46, background: color + '22', color, flexShrink: 0, fontSize: 18 }}
                        >
                          {initial}
                        </div>
                        <div className='d-flex gap-1'>
                          <Button color='light' size='sm' className='py-1 px-2' title='Ver QR Code' onClick={() => openQrModal(conn)}>
                            <Grid size={13} />
                          </Button>
                          {!isAdmin && (
                            <Button color='light' size='sm' className='py-1 px-2' title='Editar' onClick={() => openEditModal(conn)}>
                              <Edit size={13} />
                            </Button>
                          )}
                          {!isAdmin && (
                            <Button color='light' size='sm' className='py-1 px-2' title='Excluir' onClick={() => setDeleteModal(conn)}>
                              <Trash2 size={13} className='text-danger' />
                            </Button>
                          )}
                        </div>
                      </div>

                      <h6 className='mb-1 f-w-600' style={{ color }}>{conn.display_name || conn.instance_name}</h6>
                      <p className='f-12 text-muted mb-2'>{conn.instance_name}</p>

                      {conn.phone_number && (
                        <div className='d-flex align-items-center gap-1 mb-2'>
                          <Phone size={11} className='text-muted' />
                          <small className='text-muted'>{conn.phone_number}</small>
                        </div>
                      )}

                      <div className='d-flex align-items-center gap-2 mt-2'>
                        <span style={{
                          display: 'inline-block', width: 9, height: 9, borderRadius: '50%',
                          background: st.color,
                          animation: st.pulse ? 'pulse 1.5s infinite' : 'none',
                          boxShadow: st.pulse ? `0 0 0 2px ${st.color}44` : 'none',
                        }} />
                        <span className='f-12' style={{ color: st.color }}>{st.label}</span>
                      </div>

                      {isAdmin && conn.tenant_name && (
                        <small className='text-muted d-block mt-2'>{conn.tenant_name}</small>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}

        <div className='mt-1'>
          <small className='text-muted'>{total} conexão{total !== 1 ? 'ões' : ''}</small>
        </div>

      </Container>

      {/* ── Modal QR Code ───────────────────────────────────────────────────── */}
      <Modal isOpen={!!qrModal} toggle={closeQrModal}>
        <ModalHeader toggle={closeQrModal}>{qrModal?.display_name || qrModal?.instance_name}</ModalHeader>
        <ModalBody>
          {qrModal?.status === 'connected' ? (
            <div className='text-center py-4'>
              <div className='rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3'
                style={{ width: 72, height: 72, background: '#54ba4a22', color: '#54ba4a' }}>
                <Wifi size={36} />
              </div>
              <h6 className='mb-1' style={{ color: '#54ba4a' }}>WhatsApp Conectado!</h6>
              {qrModal?.phone_number && <p className='text-muted f-12'>{qrModal.phone_number}</p>}
            </div>
          ) : (
            <>
              <div className='d-flex align-items-center justify-content-center rounded-3 p-4 mb-3'
                style={{ background: '#f8f9fa', minHeight: 240 }}>
                {qrLoading && !qrBase64 ? (
                  <div className='text-center'>
                    <RefreshCw size={24} className='text-muted mb-2' style={{ animation: 'spin 1s linear infinite' }} />
                    <p className='text-muted f-12 mb-0'>Gerando QR code...</p>
                  </div>
                ) : qrBase64 ? (
                  <img src={qrBase64} alt='QR Code' style={{ width: 200, height: 200, objectFit: 'contain' }} />
                ) : (
                  <p className='text-muted f-12 text-center mb-0'>
                    Não foi possível gerar o QR code.<br />Tente fechar e abrir novamente.
                  </p>
                )}
              </div>
              <p className='text-center text-muted f-12 mb-2'>
                Abra o WhatsApp → Dispositivos Conectados → Conectar dispositivo
              </p>
              <div className='text-center'>
                <span className='badge rounded-pill' style={{ background: '#7366ff22', color: '#7366ff', fontSize: 11 }}>
                  <RefreshCw size={10} className='me-1' />
                  Atualiza em {qrTimer}s
                </span>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={closeQrModal}>Fechar</Button>
        </ModalFooter>
      </Modal>

      {/* ── Modal Adicionar ─────────────────────────────────────────────────── */}
      <Modal isOpen={addModal} toggle={() => setAddModal(false)}>
        <ModalHeader toggle={() => setAddModal(false)}>Nova conexão</ModalHeader>
        <ModalBody>
          {addError && <div className='alert alert-danger small'>{addError}</div>}
          <form onSubmit={handleAdd} id='addConnForm'>
            {isAdmin && (
              <FormGroup>
                <Label className='small fw-medium'>Cliente (tenant) *</Label>
                <Input type='select' bsSize='sm' required value={addForm.tenant_id}
                  onChange={e => setAddForm(f => ({ ...f, tenant_id: e.target.value }))}>
                  <option value=''>Selecione o cliente</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.name} ({t.email})</option>)}
                </Input>
              </FormGroup>
            )}
            <FormGroup>
              <Label className='small fw-medium'>Nome da instância</Label>
              <Input type='text' bsSize='sm' required placeholder='ex: artesanal-vendas-1'
                innerRef={addInstanceRef}
                value={addForm.instance_name}
                onChange={e => setAddForm(f => ({ ...f, instance_name: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
              />
              <small className='text-muted'>Usado internamente na Evolution API</small>
            </FormGroup>
            <FormGroup>
              <Label className='small fw-medium'>Nome de exibição</Label>
              <Input type='text' bsSize='sm' placeholder='ex: WhatsApp Vendas'
                value={addForm.display_name}
                onChange={e => setAddForm(f => ({ ...f, display_name: e.target.value }))}
              />
            </FormGroup>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={() => setAddModal(false)}>Cancelar</Button>
          <Button color='primary' type='submit' form='addConnForm' disabled={addLoading}>
            {addLoading ? 'Criando...' : 'Criar conexão'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── Modal Editar ────────────────────────────────────────────────────── */}
      <Modal isOpen={!!editModal} toggle={() => setEditModal(null)}>
        <ModalHeader toggle={() => setEditModal(null)}>Editar conexão</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label className='small fw-medium'>Nome de exibição</Label>
            <Input type='text' bsSize='sm' value={editName}
              onChange={e => setEditName(e.target.value)} autoFocus />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={() => setEditModal(null)}>Cancelar</Button>
          <Button color='primary' disabled={editLoading} onClick={handleEdit}>
            {editLoading ? 'Salvando...' : 'Salvar'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── Modal Excluir ───────────────────────────────────────────────────── */}
      <Modal isOpen={!!deleteModal} toggle={() => setDeleteModal(null)}>
        <ModalHeader toggle={() => setDeleteModal(null)}>Excluir conexão?</ModalHeader>
        <ModalBody>
          A instância <strong>{deleteModal?.display_name}</strong> será removida permanentemente da Evolution API.
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={() => setDeleteModal(null)}>Cancelar</Button>
          <Button color='danger' onClick={handleDelete}>Excluir</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
